import React, { useCallback, memo, useRef, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, Pressable, Platform } from 'react-native';
import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { colorPalette } from '../../../core/config/color.config';
import { RNEText } from '../../components';
import { debounce } from 'lodash';
import { width, height, totalSize } from 'react-native-dimension';

const ConsultationTabBar = ({
  state,
  descriptors,
  navigation,
}: MaterialTopTabBarProps) => {
  // Track the latest navigation target to prevent stale events
  const latestNavigationRef = useRef<string | null>(null);

  // Debounced navigation handler
  const handlePress = useCallback(
    debounce((routeName: string, isFocused: boolean, routeKey: string) => {
      // console.log(routeName, isFocused, routeKey)
      // Skip if already focused or same as last navigation
      if (isFocused || latestNavigationRef.current === routeName) {
        return;
      }
      latestNavigationRef.current = routeName;

      const event = navigation.emit({
        type: 'tabPress',
        target: routeKey,
        canPreventDefault: true,
      });

      if (!event.defaultPrevented) {
        navigation.navigate(routeName);
      }
    }, 400, { leading: true, trailing: false }),
    [navigation]
  );

  useEffect(()=>{
    if(state.index === 0){
      latestNavigationRef.current = 'ongoing';
    }else if(state.index === 1){
      latestNavigationRef.current = 'pending';
    }else if(state.index === 2){
      latestNavigationRef.current = 'history';
    }
  },[state])

  return (
    <View style={styles.tabRoot}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const label =
          options.tabBarLabel !== undefined
            ? options.tabBarLabel
            : options.title !== undefined
            ? options.title
            : route.name;

        const isFocused = state.index === index;

          // Minimal animation for tab content
          // const tabAnimatedStyle = useAnimatedStyle(() => ({
          //   opacity: withTiming(isFocused ? 1 : 0.7, { duration: 50 }),
          // }));

          // // Animation for indicator
          // const indicatorAnimatedStyle = useAnimatedStyle(() => ({
          //   opacity: withTiming(isFocused ? 1 : 0, { duration: 50 }),
          // }));

        const onLongPress = () => {
          navigation.emit({
            type: 'tabLongPress',
            target: route.key,
          });
        };

        return (
          <View key={route.key} style={styles.tabContainer}>
            <Pressable
              accessibilityRole="button"
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={options.tabBarAccessibilityLabel}
              testID={options.tabBarTestID}
              onPress={() => handlePress(route.name, isFocused, route.key)}
              onLongPress={onLongPress}
              style={styles.tabContent}
              // activeOpacity={0.75}
            >
              <Animated.View
                style={[
                  styles.tabContentWrapper,
                  tabAnimatedStyle,
                  {
                    backgroundColor: isFocused
                      ? colorPalette.white
                      : colorPalette.primary,
                  },
                ]}
              >
                <RNEText
                  label={label.toString()}
                  color={isFocused ? 'primary' : 'white'}
                  variant="button"
                  style={{
                    fontFamily: isFocused ? 'Quicksand-Bold' : 'Quicksand-Medium',
                  }}
                />
              </Animated.View>
            </Pressable>

            {/* <Animated.View
              style={[
                styles.tabIndicator,
                isFocused && styles.indicatorActive,
                indicatorAnimatedStyle,
              ]}
            /> */}
            <Animated.View style={[styles.tabIndicator,  isFocused && styles.indicatorActive,indicatorAnimatedStyle]} />
          </View>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  tabRoot: {
    flexDirection: 'row',
    paddingHorizontal: width(3.5),
    paddingTop: 12,
    backgroundColor: colorPalette.primary,
    height: 68,
  },
  tabContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  tabContent: {
    flex: 1,
  },
  tabContentWrapper: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 36,
  },
  tabIndicator: {
    height: 4,
    width: '80%', // Ensure indicator is wide enough to be visible
    backgroundColor: 'transparent',
    alignSelf: 'center',
  },
  indicatorActive: {
    width: 0,
    height: 0,
    backgroundColor: 'transparent',
    borderStyle: 'solid',
    borderLeftWidth: 1,
    borderRightWidth: 12,
    borderBottomWidth: 12,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: colorPalette.white,
  },
});

export default memo(ConsultationTabBar);